import type { TFootnoteElement } from '@alpod-ai-writer/plate-plugins/footnotes';
import { FootnotesPlugin, useFootnoteOpenState, useFootnotesOffset } from '@alpod-ai-writer/plate-plugins/footnotes/react';
import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import { cn } from '@udecode/cn';
import type { Value } from '@udecode/plate';
import { useEditorPlugin, useEditorReadOnly, useHotkeys, useOnClickOutside, usePluginOption, useScrollRef } from '@udecode/plate/react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';

import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useInlineEditor } from '../../hooks/useInlineEditor.hook';
import { InlineEditor } from './inlineEditor.component';

export const FootnoteEditor = () => {
    const editorContainer = useScrollRef();
    const ref = useRef<HTMLDivElement>(null);
    const readonly = useEditorReadOnly();
    const footnotesPlugin = useEditorPlugin(FootnotesPlugin);
    const footnotesOffset = useFootnotesOffset();
    const isSuggesting = usePluginOption(SuggestionPlugin, 'isSuggesting');

    const { isOpened, nodeEntry, domEl, open, close, prevId, nextId } = useFootnoteOpenState();
    const [ position, setPosition ] = useState<{ top?: number, bottom?: number }>({});
    const [ render, setRender ] = useState(false);
    const [ isVisible, setIsVisible ] = useState(false);
    const [ lastNode, setLastNode ] = useState<TFootnoteElement|null>(null);

    useEffect(() => {
        if (nodeEntry) {
            setLastNode(nodeEntry[0]);
        }
    }, [ nodeEntry ]);

    useEffect(() => {
        if (!domEl || !editorContainer?.current) {
            return;
        }

        const editorRect = editorContainer.current.getBoundingClientRect();
        const elRect = domEl.getBoundingClientRect();

        let newTop: number|undefined = undefined;
        let newBottom: number|undefined = undefined;

        newTop = elRect.top - editorRect.top + elRect.height + 8;
        const screenTop = editorRect.top + newTop;
        const screenTopPercent = screenTop / window.innerHeight;

        if (screenTopPercent > 0.6) {
            newTop = undefined;
            newBottom = editorRect.bottom - elRect.bottom + elRect.height + 8;
        }

        setPosition({
            top: newTop,
            bottom: newBottom,
        });
    }, [ domEl ]);

    useEffect(() => {
        let timeout1: ReturnType<typeof setTimeout>|undefined;
        let timeout2: ReturnType<typeof setTimeout>|undefined;

        if (domEl) {
            setRender(true);

            timeout1 = setTimeout(() => {
                setIsVisible(true);
                ref.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }, 50);
        } else {
            timeout1 = setTimeout(() => {
                setIsVisible(false);
            }, 30);
            
            timeout2 = setTimeout(() => {
                setRender(false);
            }, 350);
        }

        return () => {
            if (timeout1) {
                clearTimeout(timeout1);
            }

            if (timeout2) {
                clearTimeout(timeout2);
            }
        };
    }, [ domEl ]);

    // Track whether the click is inside a Radix popover portal (suggestion accept/reject).
    // useOnClickOutside fires on mousedown — which happens BEFORE the click event that
    // triggers acceptSuggestion. Without this guard, clicking accept closes the editor
    // before the change is processed.
    const clickingInsideRadixRef = useRef(false);

    useLayoutEffect(() => {
        if (!isOpened) return;
        const onMouseDown = (e: MouseEvent) => {
            clickingInsideRadixRef.current = !!(e.target as Element).closest('[data-radix-popper-content-wrapper]');
        };
        document.addEventListener('mousedown', onMouseDown, true);
        return () => document.removeEventListener('mousedown', onMouseDown, true);
    }, [ isOpened ]);

    useOnClickOutside(() => {
        if (clickingInsideRadixRef.current) return;
        close();
    }, {
        disabled: !isOpened,
        refs: [ ref, { current: domEl } ],
    });

    useHotkeys(
        'escape',
        () => close(),
        {
            enabled: isOpened,
            enableOnContentEditable: true,
            enableOnFormTags: true
        },
    );

    const handleValueChange = useCallback((value: Value) => {
        // Use lastNode.id instead of nodeEntry — nodeEntry becomes null immediately
        // when close() fires (mousedown on accept button triggers useOnClickOutside
        // before the click event runs acceptSuggestion), but lastNode persists.
        const id = lastNode?.id;
        if (!id) return;
        const entry = footnotesPlugin.api.footnotes.getAll().find(([n]) => n.id === id);
        if (!entry) return;
        footnotesPlugin.tf.footnotes.setContents(entry[1], value);
    }, [ lastNode, footnotesPlugin ]);

    const selectPrevious = useCallback(() => {
        if (prevId) {
            open(prevId);
        }
    }, [ prevId, open ]);

    const selectNext = useCallback(() => {
        if (nextId) {
            open(nextId);
        }
    }, [ nextId, open ]);

    if (!render || !lastNode) {
        return null;
    }

    return (
        <div
            ref={ref}
            className={cn(
                'absolute left-1 lg:left-5 right-1 lg:right-5 z-50 bg-white border border-primary-500 rounded-md overflow-hidden shadow-[0px_4px_24px_rgba(0,0,0,0.15)] flex flex-col gap-0',
                'transition-all duration-200',
                'h-[16rem] lg:h-[14rem] max-h-[80vh]',
                !isVisible && 'opacity-0 pointer-events-none translate-y-2',
                isVisible && 'opacity-100 translate-y-0',
            )}
            style={{
                top: position?.top,
                bottom: position?.bottom,
            }}
        >
            <div className='flex flex-row items-center gap-1 p-2 overflow-hidden flex-shrink-0'>
                <span className='font-medium text-sm grow pl-1'>Poznámka pod čarou</span>
                <button
                    className={cn(
                        'flex flex-row items-center gap-1 text-sm leading-none text-gray-800 rounded px-1 md:pr-2 py-1 transition-colors duration-200',
                        prevId && 'hover:text-primary-500 hover:bg-gray-100 cursor-pointer',
                        !prevId && 'opacity-50 cursor-auto'
                    )}
                    disabled={!prevId}
                    onClick={selectPrevious}
                >
                    <ChevronLeftIcon size={18} /> <span className='hidden md:inline'>Předchozí</span>
                </button>
                <span className='text-sm font-semibold w-6 text-center flex-shrink-0'>{footnotesOffset + lastNode.index + 1}</span>
                <button
                    className={cn(
                        'flex flex-row items-center gap-1 text-sm leading-none text-gray-800 rounded px-1 md:pl-2 py-1 transition-colors duration-200',
                        nextId && 'hover:text-primary-500 hover:bg-gray-100 cursor-pointer',
                        !nextId && 'opacity-50 cursor-auto'
                    )}
                    disabled={!nextId}
                    onClick={selectNext}
                >
                    <span className='hidden md:inline'>Následující</span> <ChevronRightIcon size={18} />
                </button>
                <button
                    className='flex flex-row items-center gap-1 text-sm leading-none text-gray-800 hover:text-primary-500 hover:bg-gray-100 rounded p-1 transition-colors duration-200'
                    onClick={close}
                >
                    <XIcon size={18} />
                </button>
            </div>
            <EditorWrapper
                key={lastNode.id}
                initialValue={lastNode?.footnotes}
                isSuggesting={isSuggesting}
                focus
                readOnly={readonly}
                onValueChange={handleValueChange}
                className='px-2 pb-2 flex-grow flex-shrink flex flex-col gap-0 overflow-hidden'
                containerClass='p-1 pt-2 pt-3 flex-grow flex-shrink overflow-auto'
                toolbarClass='bg-gray-100 p-1 w-full rounded-full overflow-y-hidden overflow-x-auto flex-shrink-0 mb-1'
            />
        </div>
    );
};

const EditorWrapper: React.FC<{
    initialValue: Value;
    isSuggesting?: boolean;
    focus?: boolean;
    readOnly?: boolean;
    className?: string;
    toolbarClass?: string;
    containerClass?: string;
    onValueChange?: (value: Value) => void;
}> = ({
    initialValue,
    ...props
}) => {
    const docEditor = useDocumentEditor();
    const editor = useInlineEditor({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        initialValue: initialValue,
        addTrailingBlock: true
    });

    return (
        <InlineEditor
            editor={editor}
            {...props}
        />
    );
};
